import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "../../../../api/src/shared/domain/language";

// Pure answer-rendering values shared by both GPT chat surfaces (general +
// Chat-with-Paper): the markdown normalisation, the block split that lifts
// callouts out of the prose, and the per-surface markdown class strings.

// Normalize the streamed markdown: bullet glyphs → markdown lists, the
// PRIMARY_SOURCE sentinel → the localized "see more" label, and a blank line
// before list blocks so the parser renders them.
// •, ◦ and – are a legacy model's level 1-3 list markers (#905), so only a glyph
// that opens a line (after indentation or quote markers) is rewritten.
export function cleanAnswer(answer: string, seeMoreLabel: string): string {
  let text = answer
    .replace(/^([ \t>]*)• /gm, "$1- ")
    .replace(/^([ \t>]*)◦ /gm, "$1  - ")
    .replace(/^([ \t>]*)– /gm, "$1    - ")
    .replaceAll(" ", " ")
    .replaceAll("PRIMARY_SOURCE", seeMoreLabel);

  text = text.replace(/([^\n])\n([ \t]*[-*+] )/g, "$1\n\n$2");
  text = text.replace(/([^\n])\n([ \t]*\d+\. )/g, "$1\n\n$2");

  return text;
}

// Stable id for a turn seeded from the DB transcript (before `useChat` owns it).
export function seedId(prefix: string, index: number): string {
  return `seed-${prefix}-${String(index)}`;
}

/**
 * The two highlighted panels of the DS 5.0 answer layout (Figma
 * "chat-conversation", node 6079:2013): the blue "Key points" summary and the
 * orange "Caution" warning.
 */
export type AnswerCalloutTone = "key" | "caution";

/**
 * One rendered slice of an answer. The agent writes ONE markdown string; the
 * callout panels are the GitHub-alert blockquotes inside it, lifted out here so
 * the renderer can frame them as panels instead of quotes. Splitting the STRING
 * (rather than overriding Streamdown's `blockquote`) keeps the panel detection
 * on the text — the only place the marker actually exists — instead of probing
 * an already-rendered React tree.
 */
export type AnswerBlock =
  | { kind: "markdown"; text: string }
  | {
      kind: "callout";
      tone: AnswerCalloutTone;
      // The language code written after the marker: the language of the
      // answer, which titles the panel. Null when the marker carries none; the
      // panel then takes the app's language.
      language: SupportedLanguage | null;
      text: string;
    };

// GitHub alert markers the agent may emit, mapped to the two DS panels. The
// GitHub-standard spellings are included alongside our own so a model that
// already knows the syntax lands on the right panel.
const CALLOUT_MARKERS = [
  { marker: "KEY", tone: "key" },
  { marker: "KEYPOINTS", tone: "key" },
  { marker: "KEY POINTS", tone: "key" },
  { marker: "KEY_POINTS", tone: "key" },
  { marker: "TIP", tone: "key" },
  { marker: "IMPORTANT", tone: "key" },
  { marker: "CAUTION", tone: "caution" },
  { marker: "WARNING", tone: "caution" },
] as const satisfies ReadonlyArray<{
  marker: string;
  tone: AnswerCalloutTone;
}>;

function toneOf(marker: string): AnswerCalloutTone | null {
  const upper = marker.toUpperCase();
  const found = CALLOUT_MARKERS.find((entry) => entry.marker === upper);
  return found === undefined ? null : found.tone;
}

// `> [!KEY POINTS]` opens a callout, optionally followed on the same line by the
// answer's language code (`> [!KEY POINTS] fr`); every following `>` line is
// its body.
const CALLOUT_OPENER = /^>\s*\[!([A-Za-z _-]+)\][ \t]*(.*?)\s*$/;
const QUOTE_LINE = /^>\s?(.*)$/;
// A list item on the marker line is the panel's first point, not its title.
const LIST_ITEM = /^([-*+]|\d+[.)])\s/;

/**
 * Split a cleaned answer into prose blocks and callout panels. A blockquote
 * whose first line is an unknown marker (or no marker at all) stays untouched
 * inside the surrounding markdown, so ordinary quotes still render as quotes.
 */
export function parseAnswerBlocks(
  answer: string,
  // The answer is still being written: text after a marker with no body yet may
  // be a fragment of the language code.
  streaming: boolean
): AnswerBlock[] {
  const lines = answer.split("\n");
  const blocks: AnswerBlock[] = [];
  let prose: string[] = [];

  function flushProse(): void {
    const text = prose.join("\n").trim();
    prose = [];
    if (text === "") return;
    blocks.push({ kind: "markdown", text });
  }

  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (line === undefined)
      throw new Error(
        "should not happen: unreachable state — line out of range"
      );
    const opener = CALLOUT_OPENER.exec(line);
    const marker = opener === null ? undefined : opener[1];
    const tone = marker === undefined ? null : toneOf(marker);
    if (opener === null || tone === null) {
      prose.push(line);
      index += 1;
      continue;
    }
    flushProse();
    const trailing = opener[2];
    if (trailing === undefined)
      throw new Error(
        "should not happen: the group after the marker always participates"
      );
    const code = SUPPORTED_LANGUAGES.find(
      (supported) => supported === trailing.toLowerCase()
    );
    // While the answer streams, the code counts only once its line ends: a
    // heading the agent writes against the prompt can start with one ("Es…").
    const lineComplete = index + 1 < lines.length;
    const language =
      code === undefined || (streaming && !lineComplete) ? null : code;
    const other = language === null ? trailing : "";
    // A list item on the marker line is the panel's first point.
    const body: string[] = LIST_ITEM.test(other) ? [other] : [];
    const loose = other !== "" && body.length === 0;
    index += 1;
    while (index < lines.length) {
      const next = lines[index];
      if (next === undefined)
        throw new Error(
          "should not happen: unreachable state — line out of range"
        );
      const quoted = QUOTE_LINE.exec(next);
      if (quoted === null) break;
      const content = quoted[1];
      body.push(content === undefined ? "" : content);
      index += 1;
    }
    // Other text after the marker is a heading the agent wrote on its own and
    // is dropped: the panel takes its title from the language. With no body,
    // though, a finished answer put its one point there, and a streaming one
    // may still be writing the code: hold that back until the answer ends.
    const hasBody = body.some((bodyLine) => bodyLine.trim() !== "");
    const text =
      loose && !hasBody && !streaming ? other : body.join("\n").trim();
    blocks.push({ kind: "callout", tone, language, text });
  }
  flushProse();
  return blocks;
}

// Shared block rhythm — list glyphs, indentation and vertical margins are the
// same for a General answer and an Evidence segment. Streamdown draws its lists
// `list-inside`, which wraps a bullet's second line back under the glyph;
// `list-outside` keeps the hanging indent, every line aligned on the first
// letter.
const ANSWER_LIST_CLASS =
  "gpt-chat-answer font-dm-sans font-normal [&>*:first-child]:mt-0 [&_ul]:ps-4 [&_ol]:ps-4 [&_ul]:list-outside [&_ol]:list-outside [&_ul]:list-disc [&_ol]:list-decimal [&_li>p]:my-0";

// A colour that shares its variant with a font size is written as an arbitrary
// PROPERTY (`[color:var(--color-ds-…)]`), never as `text-ds-…`. `ContentMarkdown`
// hands these strings to Streamdown, which merges them with the STOCK
// tailwind-merge — it does not know the DS `--text-*` tokens, files
// `text-gpt-answer` and `text-ds-slate-700` under one `text-*` group and keeps
// only the last. That silently dropped the body size and all three heading sizes
// from every answer, which then rendered at the browser's inherited size. An
// arbitrary property is its own group, so size and colour both survive.
/**
 * The DS 5.0 answer ramp (Figma "chat-conversation", node 6079:2013): a mint
 * `h1` synthesis title, bold mint section headings, and slate prose at 15/22.
 * Citations render as chips through `ContentMarkdown`, so links carry no
 * inline styling here.
 */
export const GPT_ANSWER_CLASS = `${ANSWER_LIST_CLASS} text-gpt-answer [color:var(--color-ds-slate-700)] [&_h1]:mt-0 [&_h1]:mb-3 [&_h1]:text-heading-lg [&_h1]:font-normal [&_h1]:[color:var(--color-ds-mint-800)] [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-publication-lg [&_h2]:font-bold [&_h2]:[color:var(--color-ds-mint-800)] [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-ui-body [&_h3]:font-semibold [&_h3]:[color:var(--color-ds-mint-800)] [&_p]:my-3 [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1.5 [&_strong]:font-semibold [&_strong]:text-ds-mint-800 [&_li::marker]:text-ds-mint-800 [&>hr]:my-4!`;

/**
 * One Evidence answer SEGMENT (a single markdown block — paragraph or bullet,
 * per the synthesis contract), rendered through `ContentMarkdown` like a
 * General answer so both modes share the same look (mint bold, list glyphs).
 * Differences from `GPT_ANSWER_CLASS` are layout-driven: block margins are
 * zeroed (the segment list's flex gap owns the rhythm), and paragraphs render
 * inline so the citation chips flow right after the segment's last sentence
 * instead of dropping to their own line. No heading ramp — the synthesis
 * prompt forbids headings.
 */
export const EVIDENCE_ANSWER_SEGMENT_CLASS = `${ANSWER_LIST_CLASS} inline text-gpt-answer [color:var(--color-ds-slate-700)] [&_p]:inline [&_p]:my-0 [&_ul]:my-0 [&_ol]:my-0 [&_li]:my-1.5 [&_strong]:font-semibold [&_strong]:text-ds-mint-800 [&_li::marker]:text-ds-mint-800`;
